import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import { useStore } from '@/features/stores/StoreContext'
import { StoreSwitcher } from '@/features/stores/StoreSwitcher'
import { Button } from '@/components/ui/button'
import { LayoutDashboard, Package, ShoppingCart, Users, Settings, Home, FolderTree, Tag, BarChart3, FileText, Webhook } from 'lucide-react'
import { cn } from '@/lib/utils'

interface BaseLayoutProps {
  children: ReactNode
}

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: Home, label: 'Homepage', path: '/homepage' },
  { icon: FolderTree, label: 'Categories', path: '/categories' },
  { icon: FileText, label: 'Category Pages', path: '/category-pages' },
  { icon: Package, label: 'Products', path: '/products' },
  { icon: ShoppingCart, label: 'Orders', path: '/orders' },
  { icon: Users, label: 'Customers', path: '/customers' },
  { icon: Tag, label: 'Discounts', path: '/discounts' },
  { icon: BarChart3, label: 'Analytics', path: '/analytics' },
  { icon: Webhook, label: 'Webhooks', path: '/webhooks' },
  { icon: Settings, label: 'Settings', path: '/settings' },
]

export function BaseLayout({ children }: BaseLayoutProps) {
  const { user, signOut } = useAuth()
  const { currentStore } = useStore()
  const location = useLocation()

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r flex flex-col">
        <div className="p-4 border-b">
          <h1 className="text-xl font-bold mb-3">Modiee Admin</h1>
          <StoreSwitcher />
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.path
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-gray-100'
                )}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            )
          })}
        </nav>

        <div className="p-4 border-t">
          <div className="text-xs text-muted-foreground mb-2">{user?.email}</div>
          <Button onClick={signOut} variant="outline" size="sm" className="w-full">
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  )
}
